import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from 'src/common/enums';
import { TaxConfigurationDeductions } from './configuration';
import { BaseLicense, License } from './licenses';
import { PaymentFees } from './payment';
import { EndpointData } from './store';

export interface TaxPaymentPeriodsState {
  data: PeriodsByType;
  error?: string;
  loading: boolean;
}

/** Model for The way Tax State is Handled */
export interface TaxState {
  // Available reporting periods fetched from the API, grouped by period type.
  taxPaymentPeriods: TaxPaymentPeriodsState;
  // Tracks whether /calculateMultiPropertyTot is in flight.
  isCalculateTaxInProgress: boolean;
  // True once /calculateMultiPropertyTot has returned successfully.
  isCalculateTaxSuccess: boolean;
  // Raw response from /calculateMultiPropertyTot; used to derive payment fees and breakdown.
  calculatedTaxData: CalculatedTot;
  // User-facing error message when tax calculation fails.
  calculatedTaxErrorMsg: string;

  // Period pre-selected before entering the tax flow (e.g. from a dashboard deep-link).
  preSelectedPeriod?: Period;

  // Form data accumulated as the user progresses through the tax reporting flow.
  taxFlowData: TaxFlowData;
  taxSteps?: unknown;

  // Lazily-loaded licenses for each period, keyed by period type then start date. Single source of truth for license display.
  licensesPerPeriod: LicensesPerPeriodByType;
  // The currently active period type (monthly or quarterly); drives which bucket selectors return.
  selectedPeriodType: PeriodType;
}
export interface PeriodLicense extends License {
  address: string;
  buckets?: Array<RevenueReport>;
  occupancy?: OccupancyReport;
  ownerName: string;
  selected?: boolean;
  totPaidStatus: 'complete' | 'incomplete';
  providers: string[];
}

export interface ExtendedPaymentPeriod extends PaymentPeriod {
  year: number;
}

export interface PeriodsByType {
  [TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY]: ExtendedPaymentPeriod[];
  [TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY]: ExtendedPaymentPeriod[];
}

export type LicensesPerPeriod = Record<string, EndpointData<PeriodLicense>>;

export type LicensesPerPeriodByType = {
  [TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY]: LicensesPerPeriod;
  [TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY]: LicensesPerPeriod;
};

export type PeriodType =
  | TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY
  | TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY;
export interface PaymentPeriod extends Period {
  dueDate?: string;
  id?: string;
  licenses: Array<PeriodLicense>;
  name: string;
  period_type?: PeriodType;
  quarter?: number; // used to identify the quarter in Quarterly Periods
  totalDueForPeriod?: number;
  hideTaxFlow?: boolean;
}

export enum ReportingMethod {
  INDIVIDUAL = 'individual',
  BULK = 'bulk',
}

export interface TaxFlowData {
  selectedLicenses: Array<PaymentPeriod>;
  reportingMethod?: ReportingMethod;
}

export interface Period {
  startDate: string;
  endDate: string;
}

/** Collection Agreement Data */

export interface BucketData extends CollectionAgreementBucket {
  deductionsConfig: TaxConfigurationDeductions;
}
export interface CollectionAgreementBucket {
  key: string;
  label: string;
  collected_taxes: string[];
}

export interface CollectionAgreementBucketWith {
  [date: string]: {
    buckets: CollectionAgreementBucket[];
  };
}

export interface CollectionAgreementBucketConfig {
  enable: boolean;
  with: CollectionAgreementBucketWith;
}

export interface RevenueReport {
  discounts?: number;
  revenue?: number;
  provider?: string;
}

export interface OccupancyReport {
  available?: number;
  occupied?: number;
}

export interface TaxReportLicense {
  buckets: Array<RevenueReport>;
  occupancy: OccupancyReport;
}

export interface TaxPeriod {
  licenses: Array<TaxReportLicense>;
}

export interface TaxDataModel {
  periods?: Array<TaxPeriod>;
}

export interface BucketError {
  discounts?: string;
  revenue?: string;
}

export interface OccupancyError {
  available?: string;
  occupied?: string;
}

export interface TaxLicenseError {
  buckets: Array<BucketError>;
  occupancy: OccupancyError;
}

export interface TaxPeriodError {
  licenses: Array<TaxLicenseError>;
}

export interface TaxDataError {
  periods?: Array<TaxPeriodError>;
}

export interface TaxableReceipt extends Record<string, number | undefined> {
  accruedInterest: number;
  latePenalties: number;
  total: number;
  totDue: number;
  credit?: number;
}

export interface TaxableActivity {
  deductions: number;
  key: string;
  sales: number;
  taxableReceipts: number;
  taxTypes: Record<string, TaxableReceipt>;
  totalDueForPeriod?: number;
}

export interface CalculatedTaxableActivity extends TaxableActivity {
  allowableDeductions: number;
  licenseNumber: string;
  address: string;
  totalDueForPeriod: number;
  provider: string;
  totalRevenue: number;
}

export interface CalculatedTotMisc {
  taxableActivities: Array<CalculatedTaxableActivity>;
}

export interface CalculatedTotDetail extends Period {
  misc: CalculatedTotMisc;
  licence_no: string;
  application_no: string;
  period: string;
}
export interface CalculatedTot extends PaymentFees {
  paymentId?: string;
  details: Array<CalculatedTotDetail>;
}

export interface CalculatedPeriodLicense
  extends Omit<BaseLicense, 'applicationNumber' | 'status'> {
  totalDueForPeriod?: number;
  providers: TaxableActivity[];
}

export interface CalculatedLicense extends Period {
  address?: string;
  label: string;
  licenses: CalculatedPeriodLicense[];
  providers: TaxableActivity[];
  licenseNumber?: string;
  totalDueForPeriod?: number;
}
